import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { ReleasePlayButton } from '@components/basics/ReleasePlayButton';
import {
  PlayContentWrapper,
  PlayIndicatorBars,
} from '@components/main-components/cards/PlayIndicatorBars';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { GoldenEggSphereSM } from '@components/main-components/media/icons/GoldenEggSphereSM';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { ReleasePillFragmentDoc } from '@graphql/reactQuery';
import { useAudioController } from '@store/audio/AudioController';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleasePill on Release {
    id
    track {
      id
    }
    coverImage {
      id
      url
    }
    title
    webappUri
    artist {
      id
      name
      webappUri
    }
    ...ReleasePlayButton
  }
`);

export const MINIMAL_IMAGE_SIZE = 32;

export const ReleasePill = ({
  release,
  goldenEgg = false,
  hideArtist = false,
}: {
  release: FragmentType<ReleasePillFragmentDoc>;
  goldenEgg?: boolean;
  hideArtist?: boolean;
}) => {
  const releaseData = getFragment(ReleasePillFragmentDoc, release);

  const {
    title,
    artist,
    track: { id: trackId },
  } = releaseData;

  const songInfo = hideArtist ? title : `${title} - ${artist?.name}`;

  const { playing, activeTrackId } = useAudioController();

  const isActiveTrack = trackId === activeTrackId;
  const isPlaying = isActiveTrack && playing;

  return (
    <Container className="group" goldenEgg={goldenEgg}>
      <ReleasePlayButton releaseFrag={releaseData} playButtonSize="xs" />
      <PlayContentWrapper>
        {isPlaying && <PlayIndicatorBars smallBars size="xs" />}
        <AccessibleLink pathnameUri={webappUriPath(releaseData.webappUri)}>
          <Title>{songInfo}</Title>
        </AccessibleLink>
      </PlayContentWrapper>
      {goldenEgg && (
        <EggWrapper>
          <GoldenEggSphereSM width={38} />
        </EggWrapper>
      )}
    </Container>
  );
};

export const Container = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  maxHeight: 20,
  gap: 8,

  h2: {
    title: 'XS',
    fontWeight: 500,
    maxWidth: 190,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },

  svg: {
    filter: 'drop-shadow(0px 2px 5px rgba(217, 166, 37, 0.8))',
  },

  '@tablet': {
    gap: 10,
    background: '$neutral100',
    padding: 9,
    borderRadius: 8,
    maxHeight: 48,
  },

  '&:hover': {
    shadow: 'M',
  },

  '@tabletLandscape': {
    '&:hover': {
      cursor: 'pointer',
    },
  },

  variants: {
    goldenEgg: {
      true: {
        paddingRight: 32,
      },
    },
  },
});

const Title = styled('h2', {
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
});

const EggWrapper = styled('div', {
  position: 'absolute',
  right: -2,
  top: -4,
});
